import React, { useEffect, useRef, useState } from 'react';
interface ScaledEmbedProps {
  src: string;
  title: string;
  /** Native width the embedded design renders at before scaling. */
  baseWidth?: number;
  /** Native height the embedded design renders at before scaling. */
  baseHeight?: number;
  /** Optional window bar rendered on top of the embed, inside the frame. */
  chrome?: React.ReactNode;
  /** Height in px of the chrome bar, reserved when sizing the frame. */
  chromeHeight?: number;
  frameClassName?: string;
}

/**
 * Renders a live iframe at a fixed native size, scales it to fit the available
 * space, and shrink-wraps the window frame tightly around it — no dead space.
 */
export function ScaledEmbed({
  src,
  title,
  baseWidth = 1120,
  baseHeight = 720,
  chrome,
  chromeHeight = 0,
  frameClassName = ''
}: ScaledEmbedProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(0);
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const update = () => {
      const {
        width,
        height
      } = el.getBoundingClientRect();
      const available = Math.max(height - chromeHeight, 0);
      setScale(Math.min(width / baseWidth, available / baseHeight));
    };
    update();
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => observer.disconnect();
  }, [baseWidth, baseHeight, chromeHeight]);
  const width = baseWidth * scale;
  const height = baseHeight * scale;
  return <div ref={containerRef} className="flex h-full w-full items-center justify-center overflow-hidden" data-id="element-200" data-mp="3j3oxpj2knsvh">
      <div className={`flex flex-shrink-0 flex-col overflow-hidden ${frameClassName}`} style={{
      width,
      opacity: scale ? 1 : 0
    }} data-id="element-201" data-mp="y3j400xn657">
        {chrome && <div className="flex-shrink-0" style={{
        height: chromeHeight
      }} data-id="element-202" data-mp="1camhdd9gz5at">
            {chrome}
          </div>}
        <div className="relative overflow-hidden" style={{
        width,
        height
      }} data-id="element-203" data-mp="18a2l0bwq05pq">
          {!loaded && <div className="absolute inset-0 flex items-center justify-center" data-id="element-204" data-mp="4z6ixxcpklj7">
              <div className="h-8 w-8 animate-spin rounded-full border-4 border-white/15 border-t-white/70" data-id="element-205" data-mp="9wycswwwyhq1" />
            </div>}
          <iframe src={src} title={title} onLoad={() => setLoaded(true)} className="absolute left-0 top-0 origin-top-left border-0" style={{
          width: baseWidth,
          height: baseHeight,
          transform: `scale(${scale})`
        }} allow="fullscreen" data-id="element-206" data-mp="evrocjsn3ynk" />
        </div>
      </div>
    </div>;
}